import 'project/polyfill'
import Link from 'next/link'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { I18NConfig } from 'next/dist/server/config-shared'

import { TAddon, TPlanSummary, TReviews } from 'common/cms-types'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import {
  heroGap,
  marginHeader,
  sectionGap,
  sectionGapSmall,
  sectionPadding,
  sectionPaddingLarge,
} from 'project/padding'
import MailPortalHighlights, {
  saasSolutionsHighlights,
} from 'components/cards/new/common/MailPortalHighlights'
import { MailCountTrust } from 'components/MailCountTrust'

import { Utils } from 'common/utils'
import { Query } from '@prismicio/types'
import { createClient } from '../../prismicio'
import { PlanSlug } from 'common/planSlugs'
import Button from 'components/forms/Button'
import HoxtonMixLogo from 'components/icons/HoxtonMixLogo'
import HeroBanner from 'components/hero/HeroBanner'
import { getPosts, getReviews } from 'common/cms-sdk'
import { getAddons, getPlans } from 'common/planData'
import HeadingAndText from 'components/HeadingAndText'
import PartnersCard from 'components/cards/new/PartnersCard'
import FeaturesCard from 'components/cards/new/FeaturesCard'
import AppPromoCard from 'components/cards/new/AppPromoCard'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import ReviewsSection from 'components/sections/ReviewsSection'
import { NextPageWithLayout } from '../../types/nextPageWithLayout'
import SubscriptionPricing from 'components/SubscriptionPricing'
import { auHomeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import NoSetupFeesSection from 'components/cards/new/NoSetupFeesSection'
import { ButtonType } from '../../types/global'
import PartnersTestimonial from 'components/cards/new/PartnerTestimonialCard'
import EmbededNewsletterForm from 'components/sections/EmbededNewsletterForm'
import VirtualOfficePromoCard from 'components/cards/new/VirtualOfficePromoCard'
import SaaSOfficeSolutionCard from 'components/cards/new/SaaSOfficeSolutionCard'
import VirtualAddressPromoCard from 'components/cards/new/VirtualAddressPromoCard'
import InnovationRecognitionSummary from 'components/cards/new/InnovationRecognitionSummary'
import { getTenantFromPath, Tenant } from 'common/tenant'
import PageContainer from 'components/util/containers/PageContainer'

export type HomePageType = {
  posts: Query<TBlogPostResult>
  plans: TPlanSummary[]
  reviews: TReviews
  addons: TAddon[]
}

const HomePage: NextPageWithLayout<HomePageType> = ({
  plans,
  posts,
  reviews,
}) => {
  const router = useRouter()
  const seoProps: SeoContainerProps['seoProps'] = {
    description:
      'Virtual business address in Sydney with mail scanning & management. Register your Australian company address and let our AI agent HaiRo™ digitise your mail.',
    openGraph: {
      description:
        'Professional Sydney CBD business address for company registration. AI-powered mail scanning, digital delivery & 24/7 online access. No setup fees.',
      url: 'https://www.hoxtonmix.com/au/',
    },
    title: 'Virtual Office Address Services Australia | Hoxton Mix',
  }
  const planSummary = plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!
  return (
    <SeoContainer
      alternate={[
        { href: '/', lang: 'en-gb' },
        { href: '/au', lang: 'en-au' },
        { href: '/', lang: 'x-default' },
      ]}
      breadcrumb={auHomeBreadcrumb()}
      seoProps={seoProps}
      tenant={Tenant.AU}
    >
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column bg-grey6',
          sectionGap,
        )}
      >
        <div className={classNames('d-flex flex-column', heroGap)}>
          <HeroBanner
            logo={
              <HoxtonMixLogo
                className='mb-5 mb-lg-8'
                containerClassName='w-lg-55'
              />
            }
            robotImage={{
              alt: 'HaiRo™ robot mascot holding mail',
              dimensions: {
                height: 992,
                width: 800,
              },
              src: '/images/new/au/robot-holding-mail.png',
            }}
            cta={
              <SubscriptionPricing
                hideRegister
                showLink
                planSummary={planSummary}
                buttonType={ButtonType.AU_HOMEPAGE_HERO}
                tenant={Tenant.AU}
                rightButton={
                  <Link
                    aria-label='Take a Product Tour'
                    className='text-decoration-none'
                    href={'/au/virtual-office-sydney#demo'}
                  >
                    <Button
                      theme={'secondary'}
                      className={classNames(
                        'w-100 text-decoration-none d-none d-xl-block px-12 w-lg-auto mt-5 mt-lg-8',
                        {},
                      )}
                    >
                      Take a Product Tour
                    </Button>
                  </Link>
                }
              />
            }
            title='Want an Impressive Virtual Office Address?'
            description={
              <div className={classNames('mb-2')}>
                <div className='fs-fluid-24-32 fw-semibold'>
                  Register your Sydney business address and let our AI agent{' '}
                  <span className='fw-bold color-pink2'>HaiRo™</span> organise,
                  digitise, and deliver your mail.
                </div>
              </div>
            }
            subtitle={<></>}
          />
          <div
            className={classNames('container d-flex flex-column', sectionGap)}
          >
            <h2
              className={classNames(
                'fs-fluid-36-80 lh-105 fw-semibold text-center',
                sectionPadding,
              )}
            >
              Our
              <span className='color-pink2'> AI-powered </span>
              mail processing agent, HaiRo™, scans your mail and delivers it
              directly to your inbox.
            </h2>
          </div>
        </div>

        <div className='container mt-4'>
          <VirtualOfficePromoCard tenant={Tenant.AU} />
        </div>
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <div>
            <HeadingAndText
              className={classNames(
                'px-lg-0 px-6 ',
                marginHeader,
                sectionPadding,
              )}
              classesForTitle='fs-fluid-36-80 lh-105'
              subtitleSize='large'
              title={`Our virtual office service includes everything in <span class="color-pink2">one simple plan</span>.`}
              subtitle={`<h3 class="fw-semibold">Let HaiRo™ take care of your mail and documents.</h3>`}
            />
            <FeaturesCard planSummary={planSummary} tenant={Tenant.AU} />
          </div>
          <div>
            <h2
              className={classNames(
                'fs-fluid-36-80 lh-105 fw-semibold text-center',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              <MailCountTrust highlightClassName='color-pink2' />
            </h2>
            <div className={marginHeader}>
              <PartnersTestimonial />
            </div>
            <div className={'text-center'}>
              <SubscriptionPricing
                planSummary={planSummary}
                buttonType={ButtonType.AU_HOMEPAGE_TESTIMONIALS_CTA}
                tenant={Tenant.AU}
              />
            </div>
          </div>
          <div
            className={classNames(
              sectionGapSmall,
              'container d-flex flex-column mt-4',
            )}
          >
            <AppPromoCard planSummary={planSummary} tenant={Tenant.AU} />
            <VirtualAddressPromoCard
              planSummary={planSummary}
              tenant={Tenant.AU}
            />
          </div>

          <NoSetupFeesSection plans={plans} tenant={Tenant.AU} />
          <div className='row gy-5'>
            <div className='col-12 col-lg-6'>
              <ContactUsCard tenant={Tenant.AU} />
            </div>
            <div className='col-12 col-lg-6'>
              <PartnersCard tenant={Tenant.AU} />
            </div>
          </div>
          <SaaSOfficeSolutionCard tenant={Tenant.AU} />
          <MailPortalHighlights
            headingSize={4}
            title='SaaS solutions for accountancies and fintech.'
          >
            {saasSolutionsHighlights.map((highlight, index) => (
              <div className='pb-4' key={index}>
                <span className='fw-semibold'>{highlight.title}</span>
                <p className='pt-4'>{highlight.content}</p>
              </div>
            ))}
            <div className='text-center'>
              <Link
                className='btn btn-lg btn-primary w-100 w-lg-auto mt-2 px-22 mt-lg-13'
                href={'/au/enterprise'}
              >
                Enterprise Solutions
              </Link>
            </div>
          </MailPortalHighlights>
        </div>
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <VirtualOfficePromoCard tenant={Tenant.AU} />
          <InnovationRecognitionSummary tenant={Tenant.AU} />
          <EmbededNewsletterForm />
        </div>

        <ReviewsSection
          data={reviews}
          pageUrl={router.pathname}
          tenant={Tenant.AU}
        />
      </main>
    </SeoContainer>
  )
}

HomePage.getLayout = (page: React.ReactNode) => (
  <PageContainer tenant={Tenant.AU}>{page}</PageContainer>
)

const PAGE_SIZE = 4
export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    // Detect tenant - pass 'au' to get AU tenant (synchronous, slug-based)
    const tenant = getTenantFromPath('au')
    const plans = await getPlans(undefined, String(tenant.id))
    const posts = await getPosts(client, 1, lang, PAGE_SIZE, undefined)
    const addons = await getAddons(true, String(tenant.id))
    const reviews = await getReviews()
    return {
      props: {
        addons,
        plans,
        posts,
        reviews,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default HomePage
